import getModeReglement from '@/services/import/bailleur/parametrage/getModeReglement';
import getMotifBlocagePaiement from '@/services/import/bailleur/parametrage/getMotifBlocagePaiement';
import getCivilite from '@/services/import/bailleur/parametrage/getCivilites';
import genererClient from '@/services/import/client/genererClient';
import completeParametrageGeneriqueNonExistant from '@/services/import/General/parametrage/completeParametrageGeneriqueNonExistant';
import {
  TYPOLOGIE_ENSEIGNE, TYPOLOGIE_GROUPE,
  TYPOLOGIE_POINT_DE_VENTE, TYPOLOGIE_AUCUN,
} from '@/configuration/tiers/tiersConf';
import { PARAMETRES_KEYS } from '@/store/modules/parametres-mutation-type';
import { createAll } from '@/api/tiers/index';
import { columns } from '@/services/import/client/templateClient';
import { isAdresseNullOrComplete, extraireLibelles } from '@/services/import/General/general';
import i18n from '@/i18n/index';

export function ErrorClientTranslate(errors) {
  if (!errors || !Array.isArray(errors)) return [];
  return errors.map(error => {
    if (error.endsWith('_IS_REQUIRED')) {
      return i18n.t('importationClient.errors.requiredPropertie', { label: error.split('_')[0] });
    }
    if (error.includes('_MAX_LENGTH_')) {
      return i18n.t('importationClient.errors.maxLengthPropertie', { label: error.split('_')[0], length: error.split('_')[error.split('_').length - 1] });
    }
    switch (error) {
      case 'CODE_IS_NOT_UNIQUE':
        return i18n.t('importationClient.errors.codeIsNotUnique');
      case 'ENSEIGNE_ID_NOT_IN_FILE':
        return i18n.t('importationClient.errors.enseigneIdNotInFile');
      case 'ENSEIGNE_ID_ON_ENSEIGNE':
        return i18n.t('importationClient.errors.enseigneIdOnEnseigne');
      case 'ENSEIGNE_ID_ON_GROUPE':
        return i18n.t('importationClient.errors.enseigneIdOnGroupe');
      case 'GROUPE_ID_ON_PDV':
        return i18n.t('importationClient.errors.groupeIdOnPdv');
      case 'GROUPE_ID_ON_GROUPE':
        return i18n.t('importationClient.errors.groupeIdOnGroupe');
      case 'GROUP_ID_NOT_IN_FILE':
        return i18n.t('importationClient.errors.groupeIdNotInFile');
      case 'ADRESSE_FACTURATION_IMCOMPLETE':
        return i18n.t('importationClient.errors.adresseFacturationIncomplete');
      case 'ADRESSE_POSTALE_IMCOMPLETE':
        return i18n.t('importationClient.errors.adressePostaleIncomplete');
      case 'MARKS_MUST_BE_ENCLOSED_IN_QUOTES_AND_SEPARATED_BY_COMMAS':
        return i18n.t('importationClient.errors.marksMustBeEnclusedInQuotesAndSeparatedByCommas');
      case 'MARKS_CANNOT_BE_DUPLICATED':
        return i18n.t('importationClient.errors.marksCannotBeDuplicated');
      default:
        return 'Un problème est survenu';
    }
  }).join(' - ');
}

function countOccurrences(arr) {
  const occurrences = [];

  // Parcourir le tableau et compter les occurrences de chaque valeur
  for (let i = 0; i < arr.length; i += 1) {
    const currentElement = arr[i];

    // Vérifier si la valeur est déjà présente dans le tableau des occurrences
    const existingElement = occurrences.find(el => el.value === currentElement);

    // Si la valeur n'est pas encore présente, l'ajouter au tableau avec un compteur initial à 1
    if (!existingElement) {
      occurrences.push({ value: currentElement, count: 1 });
    } else {
      // Si la valeur est déjà présente, incrémenter le compteur
      existingElement.count += 1;
    }
  }

  // Retourner l'objet contenant le nombre d'occurrences de chaque valeur
  return occurrences;
}

function validationLigne(ligne, codeList) {
  const error = {
    numLigne: ligne.numLigne,
    errors: [],
  };
  if (codeList.includes(ligne.code)) error.errors.push('CODE_IS_NOT_UNIQUE');
  columns.forEach(column => {
    const columnErrors = column.checkValue(ligne[column.key]);
    if (columnErrors.length > 0) error.errors = error.errors.concat(columnErrors);
  });

  if (!isAdresseNullOrComplete(ligne, 'adresseFacturation')) error.errors.push('ADRESSE_FACTURATION_IMCOMPLETE');
  if (!isAdresseNullOrComplete(ligne, 'adressePostale')) error.errors.push('ADRESSE_POSTALE_IMCOMPLETE');

  if (ligne.typologie) {
    if (ligne.typologie === TYPOLOGIE_POINT_DE_VENTE) {
      if (ligne.groupeId) error.errors.push('GROUPE_ID_ON_PDV');
      if (ligne.enseigneId && !codeList.includes(ligne.enseigneId)) error.errors.push('ENSEIGNE_ID_NOT_IN_FILE');
    }
    if (ligne.typologie === TYPOLOGIE_ENSEIGNE) {
      if (ligne.enseigneId) error.errors.push('ENSEIGNE_ID_ON_ENSEIGNE');
      if (ligne.groupeId && !codeList.includes(ligne.groupeId)) error.errors.push('GROUP_ID_NOT_IN_FILE');
    }
    if (ligne.typologie === TYPOLOGIE_GROUPE) {
      if (ligne.groupeId) error.errors.push('GROUPE_ID_ON_GROUPE');
      if (ligne.enseigneId) error.errors.push('ENSEIGNE_ID_ON_GROUPE');
    }
  }

  if (ligne.tiersMarques?.trim()) {
    ligne.tiersMarques = ligne.tiersMarques
      .trim().replace(/,$/, '')
      .trim().replace(/^,/, '')
      .trim();
    const regex = /^"([^"]+")(,"[^"]+")*$/;
    if (!regex.test(ligne.tiersMarques)) {
      error.errors.push('MARKS_MUST_BE_ENCLOSED_IN_QUOTES_AND_SEPARATED_BY_COMMAS');
    }
    const marquesClient = countOccurrences(extraireLibelles(ligne.tiersMarques));
    const doubleMarque = marquesClient.filter(x => x.count > 1);
    if (doubleMarque.length > 0) error.errors.push('MARKS_CANNOT_BE_DUPLICATED');
  }

  if (error.errors.length > 0) throw error;
  codeList.push(ligne.code);
}

export async function makeClientGenerated(file) {
  const lignes = file[0];
  const clients = [];
  const errors = [];
  const modes = [];
  const motifs = [];
  const civilites = [];
  const marques = [];
  const codes = [];

  // saut de la premiere vu qu'on a 2 lignes d'entete
  for (let i = 1; i < lignes.length; i += 1) {
    try {
      validationLigne(lignes[i], codes);
      if (lignes[i]?.modeReglement) modes.push(lignes[i].modeReglement);
      if (lignes[i]?.motifBlocagePaiement) motifs.push(lignes[i].motifBlocagePaiement);
      if (lignes[i]?.civilite) civilites.push(lignes[i].civilite);
      if (lignes[i]?.tiersMarques) {
        lignes[i].tiersMarques = extraireLibelles(lignes[i].tiersMarques);
        marques.push(...lignes[i].tiersMarques);
      }
    } catch (e) {
      errors.push(e);
    }
  }
  if (errors.length > 0) {
    errors.unshift({ total: lignes.length - 1, totalErrors: errors.length });
    throw errors;
  }

  await Promise.all([
    getModeReglement(modes),
    getMotifBlocagePaiement(motifs),
    getCivilite(civilites),
    completeParametrageGeneriqueNonExistant(PARAMETRES_KEYS.marque, marques),
  ]);

  // saut de la premiere vu qu'on 2 lignes d'entete
  for (let i = 1; i < lignes.length; i += 1) {
    const client = genererClient(lignes[i]);
    clients.push(client);
  }

  return clients;
}

export async function makeClientImport(clients, modeTest) {
  const groupes = clients.filter(c => c.typologie === TYPOLOGIE_GROUPE);
  const normal = clients.filter(c => c.typologie === TYPOLOGIE_AUCUN);
  const enseignes = clients.filter(c => c.typologie === TYPOLOGIE_ENSEIGNE);
  const pointVentes = clients.filter(c => c.typologie === TYPOLOGIE_POINT_DE_VENTE);
  await createAll(normal, modeTest, true);
  const groupesAdded = (await createAll(groupes, modeTest, true)).data.result;
  const Gkeys = {};
  for (let i = 0; i < groupesAdded.length; i += 1) {
    Gkeys[`${groupesAdded[i].referenceExterne}`] = groupesAdded[i].id;
  }
  for (let i = 0; i < enseignes.length; i += 1) {
    enseignes[i].tiersParentId = Gkeys[enseignes[i].groupeId];
  }
  const enseignesAdded = (await createAll(enseignes, modeTest, true)).data.result;
  const Ekeys = {};
  for (let i = 0; i < enseignesAdded.length; i += 1) {
    Ekeys[`${enseignesAdded[i]?.referenceExterne}`] = enseignesAdded[i].id;
  }
  for (let i = 0; i < pointVentes.length; i += 1) {
    pointVentes[i].tiersParentId = Ekeys[pointVentes[i].enseigneId];
  }
  await createAll(pointVentes, modeTest, true);
}
